import type { ItemView } from '@fall26/shared'
import { useEffect, useState } from 'react'
import { useDeleteItem, useSetScore, useUpdateItem } from '../../api/hooks.js'
import { parseScoreText, scorePlaceholder } from '../grades/score-text.js'
import { type Draft, changedFields, draftOf } from './changed-fields.js'

/**
 * One item, editable.
 *
 * A bottom sheet rather than a page: an edit is something you do to a row you
 * are looking at, and losing the list behind it costs the context that told you
 * which row you meant.
 *
 * Saving sends only the fields that changed. That is not an optimisation — the
 * server records an override for every field a PATCH names, and an override
 * permanently stops Canvas updating that field. See changed-fields.ts.
 */
export function ItemSheet({ item, onClose }: { item: ItemView; onClose: () => void }) {
  const [draft, setDraft] = useState<Draft>(() => draftOf(item))
  const [scoreText, setScoreText] = useState(item.score === null ? '' : String(item.score))
  const [confirmingDelete, setConfirmingDelete] = useState(false)
  const [note, setNote] = useState<string | null>(null)

  const update = useUpdateItem()
  const setScore = useSetScore()
  const remove = useDeleteItem()

  // Escape closes it, because on a laptop it is a dialog and that is what
  // dialogs do.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  /*
   * Hold the list still underneath.
   *
   * Without this, scrolling to the bottom of the sheet keeps going and takes
   * the 126-row list behind it with it — so closing the sheet drops me
   * somewhere I never navigated to, having lost the row I was working on.
   * `overscroll-contain` on the sheet stops the chaining; locking the body is
   * what stops a drag that starts on the scrim.
   */
  useEffect(() => {
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previous
    }
  }, [])

  const patch = changedFields(item, draft)
  const fields = Object.keys(patch)
  const score = parseScoreText(scoreText, item.maxPoints)
  // Any parseable, non-blank entry is a change; blank is a change only if a score exists.
  const scoreChanged = score.ok && (scoreText.trim() !== '' || item.score !== null)
  const dirty = fields.length > 0 || scoreChanged
  const busy = update.isPending || setScore.isPending || remove.isPending

  const save = async () => {
    if (!score.ok) return setNote(score.why)

    if (fields.length > 0) {
      const result = await update.mutateAsync({ id: item.id, patch, description: `Edit "${item.title}"` })
      if (result === null) setNote('Queued — will save when the server is back')
    }
    if (scoreChanged && score.ok) {
      await setScore.mutateAsync({ id: item.id, body: score.body, title: item.title })
    }
    onClose()
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Edit ${item.title}`}
      className="fixed inset-0 z-40 flex items-end justify-center"
    >
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-[var(--overlay)]"
      />

      <div className="safe-bottom relative flex max-h-[88dvh] w-full max-w-2xl flex-col overflow-y-auto overscroll-contain rounded-t-[var(--radius-lg)] border-t border-[var(--border)] bg-[var(--surface)] px-4 pt-3 shadow-[var(--shadow-raised)] md:mb-6 md:rounded-[var(--radius-lg)] md:border">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="tabular text-[10px] uppercase tracking-[0.1em] text-[var(--text-subtle)]">
                {item.courseId}
              </span>
              {item.gate && <Badge tone="overdue">gate</Badge>}
              {item.kind === 'exam' && <Badge tone="accent">exam</Badge>}
              {item.canvasStale && <Badge tone="unknown">not in canvas</Badge>}
              {item.linkedExternalId && <Badge tone="subtle">from canvas</Badge>}
            </div>
            <h2 className="mt-0.5 truncate text-[15px] font-medium text-[var(--text)]">{item.title}</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="tap -mr-1 -mt-1 flex items-center justify-center text-[var(--text-muted)]"
          >
            <svg viewBox="0 0 14 14" className="h-4 w-4" aria-hidden>
              <path d="M3 3l8 8M11 3l-8 8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <div className="mt-3 space-y-3">
          <Field label="Title">
            <input
              value={draft.title}
              onChange={(e) => setDraft({ ...draft, title: e.target.value })}
              className={inputClass}
            />
          </Field>

          <Field label="Detail">
            <textarea
              value={draft.detail}
              rows={3}
              onChange={(e) => setDraft({ ...draft, detail: e.target.value })}
              className={`${inputClass} resize-none`}
            />
          </Field>

          <div className="flex gap-2">
            <Field label="Due date" className="flex-1">
              <input
                type="date"
                value={draft.dueDate}
                onChange={(e) => setDraft({ ...draft, dueDate: e.target.value })}
                className={inputClass}
              />
            </Field>
            <Field label="Time" className="w-32">
              <input
                type="time"
                value={draft.dueTime}
                onChange={(e) => setDraft({ ...draft, dueTime: e.target.value })}
                className={inputClass}
              />
            </Field>
          </div>

          {draft.dueDate !== '' && draft.dueTime === '' && (
            <p className="tabular text-[11px] text-[var(--text-subtle)]">
              No time — treated as the end of that day.
            </p>
          )}

          {item.category !== null && (
            <Field label={`Score${item.categoryLabel ? ` · ${item.categoryLabel}` : ''}`}>
              <div className="flex gap-2">
                <input
                  type="text"
                  inputMode="decimal"
                  value={scoreText}
                  placeholder={scorePlaceholder(item.maxPoints)}
                  onChange={(e) => setScoreText(e.target.value)}
                  className={`${inputClass} flex-1`}
                />
                {scoreText !== '' && (
                  <button type="button" onClick={() => setScoreText('')} className={`tap ${quietButton}`}>
                    Clear
                  </button>
                )}
              </div>
              {/*
                * Blank is not zero. Clearing the field removes the score row
                * entirely, which leaves the item out of the course average --
                * scoring it 0 would drag the average down instead.
                */}
              <p className="tabular mt-1 text-[11px] text-[var(--text-subtle)]">
                {score.ok ? score.preview : score.why} · blank means not graded, which is not zero
              </p>
            </Field>
          )}
        </div>

        {item.overriddenFields.length > 0 && (
          <p className="tabular mt-3 rounded-[var(--radius)] bg-[var(--accent-wash)] px-2.5 py-2 text-[11px] leading-snug text-[var(--accent)]">
            Yours already: {item.overriddenFields.join(', ')} — Canvas asks before changing these.
          </p>
        )}

        {note && (
          <p className="tabular mt-2 text-[11.5px] text-[var(--text-muted)]" role="status">
            {note}
          </p>
        )}

        <div className="sticky bottom-0 mt-4 flex gap-2 border-t border-[var(--rule)] bg-[var(--surface)] py-3">
          <button
            type="button"
            onClick={save}
            disabled={!dirty || busy}
            className={`tap flex-1 rounded-[var(--radius)] border border-[var(--text)] bg-[var(--text)] px-4 text-[12px] font-medium uppercase tracking-[0.06em] text-[var(--text-inverted)] transition-opacity disabled:opacity-40`}
          >
            {busy ? 'Saving…' : dirty ? `Save ${fields.length + (scoreChanged ? 1 : 0)}` : 'No changes'}
          </button>

          {confirmingDelete ? (
            <button
              type="button"
              onClick={async () => {
                await remove.mutateAsync({ id: item.id, title: item.title })
                onClose()
              }}
              className={`tap ${dangerButton}`}
            >
              Delete for good?
            </button>
          ) : (
            <button type="button" onClick={() => setConfirmingDelete(true)} className={`tap ${dangerButton}`}>
              Delete
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

const inputClass =
  'tap w-full rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface-sunken)] px-3 text-[var(--text)] outline-none focus:border-[var(--accent)]'
const quietButton =
  'shrink-0 rounded-[var(--radius)] border border-[var(--border)] px-3 text-[11px] uppercase tracking-[0.06em] text-[var(--text-muted)]'
const dangerButton =
  'shrink-0 rounded-[var(--radius)] border border-[var(--overdue)]/40 bg-[var(--overdue-wash)] px-3 text-[11px] uppercase tracking-[0.06em] text-[var(--overdue)]'

function Field({
  label,
  children,
  className = '',
}: {
  label: string
  children: React.ReactNode
  className?: string
}) {
  return (
    <label className={`block ${className}`}>
      <span className="tabular mb-1 block text-[10px] uppercase tracking-[0.12em] text-[var(--text-subtle)]">
        {label}
      </span>
      {children}
    </label>
  )
}

function Badge({ tone, children }: { tone: 'overdue' | 'accent' | 'unknown' | 'subtle'; children: React.ReactNode }) {
  const tones = {
    overdue: 'bg-[var(--overdue-wash)] text-[var(--overdue)]',
    accent: 'bg-[var(--accent-wash)] text-[var(--accent)]',
    unknown: 'bg-[var(--unknown-wash)] text-[var(--unknown)]',
    subtle: 'bg-[var(--surface-sunken)] text-[var(--text-subtle)]',
  }
  return (
    <span
      className={`tabular rounded-full px-1.5 text-[10px] font-semibold uppercase tracking-[0.06em] ${tones[tone]}`}
    >
      {children}
    </span>
  )
}
